/**
 * dlg.ts — **브라우저 시스템 창을 대신하는 공용 대화상자 한 벌** (D001)
 *
 * 사장님 지시(2026-08-20): *"alert 안 나타나게 해줘. 전체공통"*
 *
 * 무엇이 문제였나
 * ---------------
 * 앱 화면에는 우리 팝업(`modal.ts` → `ti-modal`)이 있는데, **팝업 마크업이
 * 없는 문서**(뷰어·나란히 보기·랜딩)에서는 세 곳이 각각 브라우저 창으로
 * 떨어지고 있었다:
 *   · `modal.ts` — `#nx-modal-tpl` 이 없으면 `window.alert/confirm/prompt`
 *   · `doxtree.ts` — 부모가 없으면 `window.confirm/prompt`
 *   · `viewer_annot.ts` — 처음부터 `window.alert/confirm`
 * 그래서 같은 «저장하지 못했습니다» 가 어떤 길로 왔느냐에 따라 팝업으로도
 * 뜨고 시스템 창으로도 떴다.
 *
 * 왜 여기 한 벌인가(0-0)
 * ----------------------
 * · 그림(CSS)은 `nx-shell-parts.css` 의 `.nx-dlg` 한 곳이다 — 그 파일은
 *   `_shell_chrome.head_css(parts=true)` 를 통해 **모든 문서가 싣는다.**
 * · 부르는 창구는 이 파일 하나다. 세 곳이 각자 창을 만들지 않는다.
 * · 앱 화면은 그대로 `ti-modal` 을 쓴다 — 이 부품은 **그것이 없을 때만** 선다.
 *
 * 규약
 * ----
 * · 시간에 기대지 않는다(0-9 규칙 7) — 닫기 판정은 «눌렀는가»·«키를
 *   눌렀는가» 라는 사실뿐이다. setTimeout 이 하나도 없다.
 * · 되돌려주는 값은 `modal.ts` 와 **같은 모양**이다:
 *   confirm → `true | null` · prompt → `string | null`.
 */

type Btn = { label: string; primary?: boolean; value: unknown };

/** 단추 글자. **부르는 쪽이 준다** — 사전을 들고 있는 곳이 거기이기 때문이다
 *  (뷰어는 `cfg.str`, 앱은 `window.nx.t`, 트리는 `T()`). 여기서 새 사전을
 *  만들면 같은 낱말이 두 벌이 된다(0-0). 안 주면 영어로 떨어진다. */
export type DlgWords = { ok?: string; cancel?: string };

function panel(message: string, buttons: Btn[], input?: { value: string }):
    Promise<unknown> {
  return new Promise((resolve) => {
    const back = document.createElement('div');
    back.className = 'nx-dlg-back';
    const wrap = document.createElement('div');
    wrap.className = 'nx-dlg';
    wrap.setAttribute('role', 'dialog');
    wrap.setAttribute('aria-modal', 'true');

    const box = document.createElement('div');
    box.className = 'nx-dlg__panel';
    const msg = document.createElement('p');
    msg.className = 'nx-dlg__msg';
    msg.textContent = String(message == null ? '' : message);
    box.append(msg);

    let field: HTMLInputElement | null = null;
    if (input) {
      field = document.createElement('input');
      field.type = 'text';
      field.className = 'nx-dlg__in';
      field.value = String(input.value == null ? '' : input.value);
      box.append(field);
    }

    const foot = document.createElement('div');
    foot.className = 'nx-dlg__foot';
    box.append(foot);
    wrap.append(box);

    let done = false;
    const prev = document.activeElement as HTMLElement | null;
    const finish = (v: unknown): void => {
      if (done) return;
      done = true;
      document.removeEventListener('keydown', onKey, true);
      wrap.remove();
      back.remove();
      try { prev?.focus(); } catch { /* 사라진 자리면 그만이다 */ }
      resolve(v);
    };
    //: 취소 값 = **primary 가 아닌 단추**의 값(없으면 null). 시스템 창의
    //  「취소」·Esc 와 같은 뜻이 되도록 한 곳에서 정한다.
    const cancelValue = (buttons.find((b) => !b.primary) || { value: null }).value;
    const onKey = (ev: KeyboardEvent): void => {
      if (ev.key === 'Escape') { ev.preventDefault(); finish(cancelValue); return; }
      if (ev.key === 'Enter' && (!field || ev.target === field)) {
        const go = buttons.find((b) => b.primary);
        if (go) {
          ev.preventDefault();
          finish(field ? field.value : go.value);
        }
      }
    };

    for (const b of buttons) {
      const el = document.createElement('button');
      el.type = 'button';
      el.className = 'nx-dlg__btn' + (b.primary ? ' nx-dlg__btn--primary' : '');
      el.textContent = b.label;
      el.addEventListener('click', () => {
        finish(b.primary && field ? field.value : b.value);
      });
      foot.append(el);
    }

    document.addEventListener('keydown', onKey, true);
    back.addEventListener('click', () => finish(cancelValue));
    document.body.append(back, wrap);
    try { (field || foot.querySelector('button'))?.focus(); } catch { /* */ }
    if (field) field.select();
  });
}

function okWord(w?: DlgWords): string { return (w && w.ok) || 'OK'; }
function noWord(w?: DlgWords): string { return (w && w.cancel) || 'Cancel'; }

/** 알림 — 누를 것은 [확인] 하나. `window.alert()` 자리. */
export function dlgAlert(message: string, w?: DlgWords): Promise<unknown> {
  return panel(message, [{ label: okWord(w), primary: true, value: true }]);
}

/** 확인 — `true`(확인) 또는 `null`(취소). `window.confirm()` 자리. */
export function dlgConfirm(message: string, w?: DlgWords): Promise<unknown> {
  return panel(message, [
    { label: noWord(w), value: null },
    { label: okWord(w), primary: true, value: true },
  ]);
}

/**
 * ★★★★★★ F651(사장님 지시 2026-08-22: *"양쪽화면의 싱크를 위해 계산중입니다
 * 글자와 앞에 `ri-refresh-line` 아이콘 붙여서 팝업 넣어라"*)
 *
 * **알리는 판 — 누를 것이 없다.** 사람이 닫는 것이 아니라 **일이 끝나면**
 * 부르는 쪽이 걷는다. 돌려주는 것은 «걷는 함수» 하나다.
 *
 * 왜 여기인가(0-0) — 나란히 보기는 팝업 마크업이 없는 문서라 이 부품이 받는
 * 자리다(이 파일 머리말). **새 팝업을 짓지 않는다.** 그림은 위 `panel()` 과
 * **같은 `.nx-dlg` 한 벌**을 쓰고, 더한 것은 `.nx-dlg__busy`(아이콘+글자 한
 * 줄) 뿐이며 그것도 `nx-shell-parts.css` 에 있다(템플릿에 박지 않는다).
 *
 * ★ 글자는 **부르는 쪽이 준다** — 사전을 들고 있는 곳이 거기다(위 `DlgWords`
 *   와 같은 규칙). 여기서 한국어를 박으면 사전이 두 벌이 된다.
 * ★ 아이콘 이름도 부르는 쪽이 준다. 기본값은 사장님이 고르신 `ri-refresh-line`.
 * ★ 시간에 기대지 않는다(0-9 규칙 7) — 스스로 사라지는 타이머가 **없다.**
 *   «준비됐다» 는 사실을 아는 쪽이 걷는다.
 * ★ 두 번 부르면 앞의 것을 걷고 새로 세운다(막이 겹쳐 어두워지지 않는다).
 */
let busyOpen: (() => void) | null = null;

export function dlgBusy(message: string, icon = 'ri-refresh-line'): () => void {
  if (busyOpen) busyOpen();
  const back = document.createElement('div');
  back.className = 'nx-dlg-back';
  const wrap = document.createElement('div');
  wrap.className = 'nx-dlg';
  wrap.setAttribute('role', 'status');
  wrap.setAttribute('aria-live', 'polite');
  const box = document.createElement('div');
  box.className = 'nx-dlg__panel';
  const row = document.createElement('p');
  row.className = 'nx-dlg__msg nx-dlg__busy';
  const ico = document.createElement('i');
  ico.className = String(icon || 'ri-refresh-line');
  ico.setAttribute('aria-hidden', 'true');
  const txt = document.createElement('span');
  txt.textContent = String(message == null ? '' : message);
  row.append(ico, txt);
  box.append(row);
  wrap.append(box);
  document.body.append(back, wrap);
  /* ★★★★★★ F651(사장님 지시 2026-08-22: *"12초씩이나 걸릴 일이 있으면
     로딩팝에 **예상 시간을 계산해서 표시**하고 **로딩바**를 주어야 한다"*)

     막대와 남은 시간은 **값이 있을 때만 만든다.** 근거 없이 0% 막대를
     먼저 그리면 «멈춰 있다» 로 보이고, 근거 없이 «약 N초» 를 적으면 그것이
     거짓말이다(0-9). 부르는 쪽이 실제로 잰 값을 줄 때 비로소 선다. */
  let bar: HTMLElement | null = null;
  let fill: HTMLElement | null = null;
  let eta: HTMLElement | null = null;

  const close = (): void => {
    back.remove();
    wrap.remove();
    if (busyOpen === close) busyOpen = null;
  };
  /** 진행(0~100)과 남은 시간 글자. 둘 다 **없으면 안 그린다.**
   *  `pct` 가 없으면 막대를 만들지 않고, `note` 가 비면 글자를 지운다. */
  (close as any).set = (pct?: number, note?: string): void => {
    if (typeof pct === 'number' && isFinite(pct)) {
      if (!bar) {
        bar = document.createElement('div');
        bar.className = 'nx-dlg__prog';
        fill = document.createElement('i');
        bar.append(fill);
        box.append(bar);
      }
      if (fill) fill.style.width = Math.max(0, Math.min(100, pct)) + '%';
    }
    if (note != null) {
      if (!eta) {
        eta = document.createElement('p');
        eta.className = 'nx-dlg__eta';
        box.append(eta);
      }
      eta.textContent = String(note);
    }
  };
  busyOpen = close;
  return close;
}

/** 입력 — 글자 또는 `null`(취소). `window.prompt()` 자리. */
export function dlgPrompt(label: string, value: string, w?: DlgWords):
    Promise<unknown> {
  return panel(label, [
    { label: noWord(w), value: null },
    { label: okWord(w), primary: true, value: '' },
  ], { value });
}
